import React, { useEffect, useState } from 'react';
import get from 'lodash.get';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ContentLoader, List } from 'shared/components';
import { errorMsg } from 'shared/constants';
import { displayValue, formatDate } from 'shared/helpers';
import { fetchLicenses } from 'licenseUsers/actions';

const PAGE_SIZE = 20;

const getResults = (payload) =>
  Array.isArray(payload) ? payload : get(payload, 'results', []);

const getCount = (payload, results) =>
  Array.isArray(payload)
    ? results.length
    : get(payload, 'count', results.length);

const LicenseUserGroupLicensesTab = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { groupId } = useParams();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [retryToken, setRetryToken] = useState(0);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);

  useEffect(() => {
    setPage(0);
  }, [groupId]);

  useEffect(() => {
    if (!groupId) {
      return;
    }

    setLoading(true);
    setError(false);

    fetchLicenses({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      orderBy: currentSort,
      filters: { license_user_group: groupId },
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = getResults(payload);
        setLicenses(results);
        setCount(getCount(payload, results));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLicenses([]);
        setCount(0);
        setLoading(false);
      });
  }, [groupId, page, retryToken, currentSort]);

  const handleRetry = () => setRetryToken((token) => token + 1);
  const handleRowClick = (rowData) => {
    const licenseId = get(rowData, 'original.id');
    if (licenseId) {
      history.push(`/licenses/${licenseId}`);
    }
  };

  if (loading && !licenses.length) {
    return <ContentLoader text={t('Loading licenses')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserGroupDetails-error">
        <p>{t('Unable to load licenses in this group')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  const pages = Math.ceil(count / PAGE_SIZE);

  return (
    <List
      data={licenses}
      noDataText={t('No licenses in this group')}
      clickable
      handleClick={handleRowClick}
      columns={[
        {
          Header: t('Created on'),
          accessor: 'created_at',
          Cell: (cellData) => formatDate(cellData.value),
          width: 160,
        },
        {
          Header: t('License key'),
          accessor: 'license_key',
          Cell: (rowData) => {
            const isBundledLicense = get(rowData, 'original.bundle_license');
            if (isBundledLicense) {
              return '-';
            }

            const authorizationMethod = get(
              rowData,
              'original.product.authorization_method',
            );
            const isUserAuthorization = authorizationMethod === 'user';

            if (isUserAuthorization) {
              const licenseUsers = get(rowData, 'original.license_users') || [];
              const usersNum = licenseUsers.length;

              if (usersNum > 0) {
                const firstUser = get(licenseUsers, '[0].true_email');
                if (usersNum === 1) {
                  return firstUser;
                }
                return `${firstUser} (+${usersNum - 1})`;
              }

              return t(errorMsg.notAssigned);
            }

            const licenseKey = get(rowData, 'original.license_key');
            if (licenseKey) {
              return displayValue(licenseKey);
            }

            return '-';
          },
          minWidth: 220,
          sortable: false,
        },
        {
          Header: t('Product'),
          accessor: 'product.product_name',
          Cell: (cellData) => displayValue(cellData.value),
        },
        {
          Header: t('License type'),
          accessor: 'license_type',
          Cell: (cellData) => displayValue(cellData.value),
          width: 140,
        },
      ]}
      page={page}
      pages={pages}
      loading={loading}
      manual
      minRows={licenses.length || 5}
      showPagination={pages > 1}
      onPageChange={(newPage) => setPage(newPage)}
      onSortedChange={(newSorted) => setCurrentSort(newSorted)}
      defaultSorted={currentSort}
    />
  );
};

export default LicenseUserGroupLicensesTab;
